import * as React from 'react';
import { Box } from '@chakra-ui/react';
import {
  Text,
  VStack,
} from '@ayble-health/dashboard-components';

/**
 * One labelled block in the roster filter sidebar (Care Team, Member Status,
 * Appointments, …). Sections stack vertically; the heading is the only thing
 * separating them, so it carries the weight rather than a divider.
 *
 * `subsection` demotes the heading and tightens the gap above it, so the block
 * reads as belonging to the section before it rather than starting a new one.
 * Enrollment Status uses it: its two values were part of Care Coordination
 * until round 6 split them out, and they're still read together — the funnel
 * stage, then whether the member is newly arrived or gone.
 *
 * Note this is presentation only. A subsection is still its own independent
 * filter, ANDing against the section above rather than sharing its value —
 * unlike the Upcoming/Past rows inside Appointments, which really are one
 * control split across two rows. Don't read the indent as shared state.
 */
export const FilterSection = ({
  title,
  subsection = false,
  children,
}: {
  title: string;
  subsection?: boolean;
  children: React.ReactNode;
}) => (
  <VStack
    gap={3}
    align="stretch"
    mb={6}
    // Pulls up into the preceding section's bottom margin, halving the gap
    // above without needing that section to know it's being followed.
    mt={subsection ? -3 : 0}
  >
    <Text
      my={0}
      fontSize={subsection ? 'xs' : 'sm'}
      fontWeight={subsection ? 'medium' : 'semibold'}
      color={subsection ? 'muted.500' : 'secondary.500'}
      letterSpacing="0.01em"
    >
      {title}
    </Text>
    <Box>{children}</Box>
  </VStack>
);

/** A labelled control inside a section — the small caption above a dropdown. */
export const FilterField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <VStack gap={1} align="stretch" mb={3}>
    <Text my={0} fontSize="xs" color="muted.500">
      {label}
    </Text>
    {children}
  </VStack>
);
